/* eslint-disable no-magic-numbers */
import { MutableRefObject, useEffect, useMemo } from 'react';
import { View } from 'ol';
import { Extent, getCenter } from 'ol/extent';
import { useMapInstance } from '@/utils/context/mapInstanceContext';
import { usePointToProjection } from '@/utils/map/usePointToProjection';
import { useDiagramPreviewMap } from '@/components/FunctionalArea/Modal/DiagramPreview/useDiagramPreviewMap';
import { ReactionBoundingBox } from '@/components/FunctionalArea/Modal/EditReactionModal/utils/getReactionBoundingBox';

interface UseReactionDiagramPreviewResult {
  targetRef: MutableRefObject<HTMLDivElement | null>;
}

/** Breathing room, in pixels, between the reaction and the preview edges. */
const PREVIEW_PADDING = [8, 8, 8, 8];

/**
 * Preview of a whole reaction: a stationary diagram map (see
 * {@link useDiagramPreviewMap}) framing every line of the reaction at the
 * highest zoom that still fits them all in the viewport.
 *
 * The view is deliberately built without the primary map's zoom-level/extent
 * constraints — it never zooms or pans, so there is nothing to gain from
 * replicating them, and each one is another way for the resolution to drift.
 * The one constraint that matters is `minResolution`: it stops the fit from
 * zooming in past the primary map's own maximum zoom for small reactions, which
 * would otherwise render glyphs larger than the diagram ever shows them (their
 * scale is `minResolution / resolution`, see `BaseMultiPolygon.getStyle`).
 */
export const useReactionDiagramPreview = (
  boundingBox: ReactionBoundingBox | undefined,
): UseReactionDiagramPreviewResult => {
  const { mapInstance } = useMapInstance();
  const pointToProjection = usePointToProjection();

  const extent = useMemo((): Extent | undefined => {
    if (!boundingBox) {
      return undefined;
    }
    // The projection can flip the y axis, so normalise after projecting rather
    // than assuming min/max survive the transform.
    const [x1, y1] = pointToProjection({ x: boundingBox.minX, y: boundingBox.minY });
    const [x2, y2] = pointToProjection({ x: boundingBox.maxX, y: boundingBox.maxY });
    return [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)];
  }, [boundingBox, pointToProjection]);

  const maxZoomResolution = useMemo(() => {
    if (!mapInstance) {
      return undefined;
    }
    const primaryView = mapInstance.getView();
    return primaryView.getResolutionForZoom(primaryView.get('originalMaxZoom'));
  }, [mapInstance]);

  const view = useMemo(() => {
    if (!extent || !mapInstance || maxZoomResolution === undefined) {
      return undefined;
    }
    return new View({
      center: getCenter(extent),
      resolution: maxZoomResolution,
      projection: mapInstance.getView().getProjection(),
    });
  }, [extent, mapInstance, maxZoomResolution]);

  const { targetRef, previewMapRef } = useDiagramPreviewMap(view);

  // The fit depends on the pixel size of the container, which isn't known until
  // the map is mounted and can change with the modal, so it runs here rather
  // than when the view is built.
  useEffect(() => {
    const previewMap = previewMapRef.current;
    const target = targetRef.current;
    if (!previewMap || !target || !view || !extent || maxZoomResolution === undefined) {
      return undefined;
    }

    const fitToExtent = (): void => {
      previewMap.updateSize();
      const size = previewMap.getSize();
      if (!size || size[0] === 0 || size[1] === 0) {
        return;
      }
      view.fit(extent, {
        size,
        padding: PREVIEW_PADDING,
        minResolution: maxZoomResolution,
      });
    };

    fitToExtent();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(fitToExtent);
    observer.observe(target);
    return () => observer.disconnect();
  }, [view, extent, maxZoomResolution, previewMapRef, targetRef]);

  return { targetRef };
};
